<template>
  <div class="tab-container">
    <div class="tab-header">
      <div class="d-flex flex-column">
        <span class="ap-title ap-title__main">{{ $t('userConfiguration.defaultAccesses') }}</span>
        <span class="ap-subtitle ap-subtitle__main">{{ $t('userConfiguration.accessesSub') }}</span>
      </div>
    </div>
    <div class="tab-body">
      <div v-if="userIsPersonnel">
        <b-row class="mx-0">
          <b-col>
            <b-row class="py-2">
              <b-col class="pl-0">
                <div class="d-flex flex-column">
                  <span class="ap-title ap-title__section">
                    <i
                      class="section-icon far fa-hospital"
                      aria-hidden="true"
                    />
                    {{ $t('userConfiguration.accessesSub') }}
                  </span>
                </div>
              </b-col>
            </b-row>
            <b-row class="py-2">
              <b-col class="d-flex align-items-center pl-0">
                <div class="d-flex flex-column">
                  <span class="option">{{ $t('userConfiguration.selectCenter') }}</span>
                </div>
              </b-col>
              <b-col
                cols="5"
                class="d-flex justify-content-end pr-0"
              >
                <vt-combo-select
                  v-model="userDefaultAccess.defaultLab"
                  v-validate
                  :allow-empty="false"
                  track-by="labId"
                  :options="laboratories"
                  label="labName"
                />
              </b-col>
            </b-row>
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <div class="horizontal-divider my-1" />
          </b-col>
        </b-row>
        <b-row class="mx-0 py-3">
          <b-col>
            <b-row class="py-2">
              <b-col class="pl-0">
                <div class="d-flex flex-column">
                  <span class="ap-title ap-title__section">
                    <i
                      class="section-icon far fa-home"
                      aria-hidden="true"
                    />
                    {{ $t('userConfiguration.defaultModule') }}
                  </span>
                </div>
              </b-col>
            </b-row>
            <b-row>
              <b-col class="pl-0">
                <div class="col-form-label">
                  {{ $t('userConfiguration.center') }}
                </div>
              </b-col>
              <b-col cols="5">
                <div class="col-form-label">
                  {{ $t('userConfiguration.module') }}
                </div>
              </b-col>
            </b-row>
            <b-row class="scrollable-area">
              <b-col>
                <b-row
                  v-for="(center, index) in userDefaultAccess.defaultLabModules"
                  :key="index"
                  class="option-row bottom-bordered py-2"
                >
                  <b-col class="d-flex align-items-center pl-0">
                    <div class="d-flex flex-column">
                      <span class="option">{{ center.laboratory.labName }}</span>
                    </div>
                  </b-col>
                  <b-col
                    cols="5"
                    class="d-flex justify-content-end pr-0"
                  >
                    <vt-combo-select
                      v-validate
                      :value="center"
                      track-by="moduleCode"
                      :allow-empty="false"
                      :options="getLabModules(center.laboratory.labId)"
                      label="label"
                      @select="setDefaultLabModule($event, center.laboratory)"
                    />
                  </b-col>
                </b-row>
              </b-col>
            </b-row>
          </b-col>
        </b-row>
      </div>
      <div v-else>
        <b-row class="mx-0">
          <b-col>
            <b-row class="py-2">
              <b-col class="pl-0">
                <div class="d-flex flex-column">
                  <span class="ap-title ap-title__section">
                    <i
                      class="section-icon far fa-hospital"
                      aria-hidden="true"
                    />
                    {{ $t('userConfiguration.defaultModule') }}
                  </span>
                </div>
              </b-col>
            </b-row>
            <b-row class="py-2">
              <b-col class="d-flex align-items-center pl-0">
                <div class="d-flex flex-column">
                  <span class="option">{{ $t('userConfiguration.selectModule') }}</span>
                </div>
              </b-col>
              <b-col
                cols="5"
                class="d-flex justify-content-end pr-0"
              >
                <vt-combo-select
                  v-validate
                  :value="userDefaultAccess"
                  :allow-empty="false"
                  :options="getLabModules(null)"
                  track-by="moduleCode"
                  label="label"
                  @select="setPetitionerDefaultLabModule($event)"
                />
              </b-col>
            </b-row>
          </b-col>
        </b-row>
      </div>
    </div>
    <div class="tab-footer">
      <vt-loading-button
        class="btn--primary btn-sm"
        :disabled="!modelValidated"
        :params="{ promise: save }"
      >
        <i
          class="far fa-save mr-2"
          aria-hidden="true"
        />
        {{ $t('global.save') }}
      </vt-loading-button>
    </div>
  </div>
</template>

<script>
import sessionStore from '@/session/sessionStore'
import ModuleResource from '@/api/resources/ModuleResource'
import { cloneDeep } from 'lodash'
import { uiComponentCustomConfigService } from '@/api/services/common/ui-component/custom-config/ui-component-custom-config.service'
import { UserType } from '@/api/resources/common/initialization/common-initialization.dto.ts'

export default {
    name: 'ModalsUserConf',
    data() {
        return {
            userDefaultAccess: {},
            uiComponentCode: null
        }
    },
    computed: {
        laboratories() {
            return this.$store.getters.laboratoriesConfiguration.map(laboratoryConfiguration => laboratoryConfiguration.laboratory)
        },
        userIsPersonnel() {
            return this.$store.getters.userInformation.userType === UserType.PERSONNEL
        },
        modelValidated() {
            return this.$_someFieldIsDirty(this.fields)
        }
    },
    async created() {
        const userDefaultAccessUiComponent =
            await this.$_toCallUnique(uiComponentCustomConfigService.get(sessionStore.state.userDefaultAccessUiComponentCode))
        if (userDefaultAccessUiComponent) {
            this.uiComponentCode = userDefaultAccessUiComponent.componentCode
            await sessionStore.loadPersonnelUserDefaultAccess(userDefaultAccessUiComponent)
            this.userDefaultAccess = cloneDeep(this.$store.getters.defaultAccess)
            if (this.userIsPersonnel) {
                this.userDefaultAccess.defaultLabModules =
                    this.userDefaultAccess?.defaultLabModules.map((access) => {
                        const modules = this.getLabModules(access.laboratory.labId)
                        const foundModule = modules.find((module) => module?.moduleCode === access.moduleCode)
                        access.label = foundModule.getLabel() || access.label
                        return access
                    })
            }
        }
    },
    methods: {
        setDefaultLabModule(module, laboratory) {
            const userDefaultLabModule = this.userDefaultAccess.defaultLabModules.find(labModule => labModule.laboratory.labId === laboratory.labId)
            userDefaultLabModule.moduleCode = module.moduleCode
            userDefaultLabModule.label = module.label
        },
        setPetitionerDefaultLabModule(module) {
            this.userDefaultAccess.moduleCode = module.moduleCode
            this.userDefaultAccess.label = module.label
        },
        async save() {
            const response = await this.$_toCallUnique(sessionStore.saveUserDefaultAccess(this.userDefaultAccess))
            if (response) {
                this.showToastSuccessGlobal()
                this.$_resetValidator(this.$validator)
            }
        },
        getLabModules(labId) {
            return ModuleResource.getModulesFromLaboratoryConfiguration(this.$store.getters.laboratoriesConfiguration, labId).concat(ModuleResource.getDashboardPseudoModule())
        }
    }
}

</script>
